/*
 * Toycycle — "Shop by Play Kit" age rail.
 *
 * Paging arrows for sections/tc-play-kit-row.liquid. Deliberately not Dawn's
 * SliderComponent: that bails out unless the section renders its own button
 * markup, and it carries page-counter/looping logic this row doesn't use.
 *
 * The row is fully usable without this file — the track is a native scroller,
 * so touch, trackpad, and keyboard all work if the script never loads. The
 * arrows stay hidden until the JS confirms the track actually overflows.
 */
if (!customElements.get('tc-kit-rail')) {
  customElements.define(
    'tc-kit-rail',
    class TcKitRail extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.nav = this.querySelector('[data-nav]');
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        if (!this.track || !this.nav || !this.prev || !this.next) return;

        this.sync = this.sync.bind(this);

        this.prev.addEventListener('click', () => this.page(-1));
        this.next.addEventListener('click', () => this.page(1));
        this.track.addEventListener('scroll', this.sync, { passive: true });

        // Card widths are percentage-based, so the overflow state changes with
        // the viewport as well as with image load.
        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(this.sync);
          this.observer.observe(this.track);
        } else {
          window.addEventListener('resize', this.sync);
        }

        this.revealCurrent();
        this.sync();
      }

      // On a kit's own collection page, start the track with that kit in view,
      // one card in from the left so its predecessor is visible too. Sets
      // scrollLeft on the track only, so the page itself never jumps.
      revealCurrent() {
        const current = this.track.querySelector('[aria-current="page"]');
        if (!current) return;
        const item = current.closest('li');
        const prev = item.previousElementSibling;
        const anchor = prev || item;
        // Rects rather than offsetLeft: the track isn't positioned, so the items'
        // offsetParent is some ancestor. Padding-inline is the mobile edge bleed.
        const pad = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
        const delta = anchor.getBoundingClientRect().left - this.track.getBoundingClientRect().left - pad;
        // 'instant' overrides the track's CSS scroll-behavior: smooth on first paint.
        this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: 'instant' });
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        window.removeEventListener('resize', this.sync);
      }

      get maxScroll() {
        return this.track.scrollWidth - this.track.clientWidth;
      }

      page(direction) {
        // Just under a full viewport keeps a card of context between pages.
        const step = Math.max(1, Math.round(this.track.clientWidth * 0.8));
        this.track.scrollBy({
          left: step * direction,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }

      sync() {
        // 2px of slack absorbs sub-pixel rounding at fractional card widths,
        // which otherwise leaves "next" enabled forever at the end of the track.
        const slack = 2;
        const overflows = this.maxScroll > slack;

        this.nav.hidden = !overflows;
        if (!overflows) return;

        this.prev.disabled = this.track.scrollLeft <= slack;
        this.next.disabled = this.track.scrollLeft >= this.maxScroll - slack;
      }
    }
  );
}
